// Insights (docs/cockpit/spec.md, under Watch): for each Value Driver sub-driver, the Owner's ranked ones first, how
// each KPI moved over the months asked, in words and as a small line chart, and the records that moved it most,
// one tap from the Run behind each (or the record, when it has no Run). It reads only `GET /api/insights`. A KPI
// not measured yet says what it needs instead of a chart; nothing missing is ever shown as 0. A KPI a person's
// Roles hide is not in the answer, so it leaves no empty slot.
import { useEffect, useState, type MouseEvent } from "react";
import type { Api, Insights as InsightsView, InsightsContributor, InsightsKpi } from "../api.js";
import { useLanguage, useWords } from "../i18n/words.js";
import { PAGES, type Page } from "../shell/nav.js";
import { Trend } from "./Trend.js";
import { change, monthName, recordAt, valueWords } from "./words.js";

/** Opens the record a contributor with no Run names: a list page, searched for its number. */
export type OpenRecord = (page: Page, words: string) => void;

/** Plain left clicks stay in the app; others open as the browser does. */
const inApp = (event: MouseEvent) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

function Contributor({
  contributor,
  onOpenRun,
  onOpenRecord,
}: {
  contributor: InsightsContributor;
  onOpenRun: (run: string) => void;
  onOpenRecord: OpenRecord;
}) {
  const { run } = contributor;
  if (run !== null)
    return (
      <a
        className="moved"
        href={`${PAGES.runs.path}/${run}`}
        onClick={(event) => {
          if (!inApp(event)) return;
          event.preventDefault();
          onOpenRun(run);
        }}
      >
        {contributor.reason}
      </a>
    );
  const { page, words } = recordAt(contributor.object, contributor.reason);
  const href = words ? `${PAGES[page].path}?${new URLSearchParams({ q: words }).toString()}` : PAGES[page].path;
  return (
    <a
      className="moved"
      href={href}
      onClick={(event) => {
        if (!inApp(event)) return;
        event.preventDefault();
        onOpenRecord(page, words);
      }}
    >
      {contributor.reason}
    </a>
  );
}

function Kpi({
  kpi,
  onOpenRun,
  onOpenRecord,
}: {
  kpi: InsightsKpi;
  onOpenRun: (run: string) => void;
  onOpenRecord: OpenRecord;
}) {
  const w = useWords();
  const { language } = useLanguage();
  if (kpi.status === "not_measured")
    return (
      <article className="kpi">
        <h3>{w.insightsKpiName(kpi.name)}</h3>
        <p className="kpi-reason">{w.insightsReason(kpi.reason)}</p>
      </article>
    );
  const latest = kpi.values.at(-1);
  // Some figures come only a few times a month, e.g. a margin once an order closes: the latest month's until then.
  const measured = kpi.values.findLast((v): v is { month: string; value: number } => v.value !== null);
  const moved = change(kpi, language);
  const latestMonth = monthName(latest?.month ?? "", "long", language);
  return (
    <article className="kpi">
      <h3>{w.insightsKpiName(kpi.name)}</h3>
      {latest && latest.value !== null ? (
        <p className="kpi-now">
          <b>{valueWords(latest.value, kpi.unit, language)}</b>
          {moved ? `, ${moved}` : null}
        </p>
      ) : (
        <p className="kpi-now">
          {w.insightsNoFigureYet(latestMonth)}
          {measured
            ? w.insightsLastFigure(
                monthName(measured.month, "long", language),
                valueWords(measured.value, kpi.unit, language),
              )
            : null}
        </p>
      )}
      <Trend name={w.insightsKpiName(kpi.name)} unit={kpi.unit} values={kpi.values} />
      <h4>{w.insightsWhatMovedIt}</h4>
      {kpi.contributors.length > 0 ? (
        <ul className="moved-list">
          {kpi.contributors.map((c) => (
            <li key={`${c.object}:${c.record}`}>
              <Contributor contributor={c} onOpenRun={onOpenRun} onOpenRecord={onOpenRecord} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="small muted">{w.insightsNothingStoodOut(latestMonth)}</p>
      )}
    </article>
  );
}

export function Insights({
  api,
  onOpenRun,
  onOpenRecord,
}: {
  api: Pick<Api, "insights">;
  onOpenRun: (run: string) => void;
  onOpenRecord: OpenRecord;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const [insights, setInsights] = useState<InsightsView | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  useEffect(() => {
    api.insights().then(setInsights, () => setProblem(w.insightsLoadFailed));
  }, [api]);

  if (!insights) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.insightsLoading}</p>;
  const shown = insights.subDrivers.filter((s) => s.kpis.length > 0);
  const [from, to] = [insights.months[0], insights.months.at(-1)];
  return (
    <>
      <div className="hello">
        <h2>{w.insightsHeading}</h2>
        <p className="muted">
          {from && to ? w.insightsRange(monthName(from, "long", language), monthName(to, "long", language)) : ""}
          {w.insightsIntro}
        </p>
      </div>
      {shown.length === 0 ? (
        <p className="muted">{w.insightsEmpty}</p>
      ) : (
        shown.map((sub) => (
          <section key={sub.id} className="insight" aria-labelledby={`insight-${sub.id}`}>
            <h2 id={`insight-${sub.id}`}>{sub.plain_name}</h2>
            <div className="kpis">
              {sub.kpis.map((kpi) => (
                <Kpi key={kpi.name} kpi={kpi} onOpenRun={onOpenRun} onOpenRecord={onOpenRecord} />
              ))}
            </div>
          </section>
        ))
      )}
    </>
  );
}
